import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
  Tooltip,
  useColorModeValue as mode,
  VStack,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { synchronizationAction } from 'actions';
import { updateCurrentConnection } from 'actions/synchronization';
import { Select } from 'chakra-react-select';
import AmatriumSkeleton from 'components/AmatriumSkeleton';
import Empty from 'components/Empty';
import ReactTable from 'components/ReactTable';
import { RootState } from 'helpers/store';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdReturnLeft } from 'react-icons/io';
import { Link, useHistory } from 'react-router-dom';

type Projects = {
  name: string;
  display_name: string;
};

const ProjectList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { connections, projects, isLoading, current } = useAppSelector(
    (state: RootState) => state.synchronization
  );

  useEffect(() => {
    if (current.connection_id) {
      dispatch(synchronizationAction.fetchProjectList({ connection_id: current.connection_id }));
    }
  }, [current.connection_id, dispatch]);

  useEffect(() => {
    if (connections?.list?.length && !current.connection_id) {
      dispatch(updateCurrentConnection(connections.list[0].id));
    }
  }, [dispatch, current.connection_id, connections]);

  useEffect(() => {
    dispatch(synchronizationAction.fetchConnectionList());
    return () => {
      dispatch(synchronizationAction.resetSyncProject());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(synchronizationAction.resetProject());
  }, []);

  const columns = useMemo<ColumnDef<Projects>[]>(
    () => [
      {
        id: 'index',
        accessorKey: 'index',
        header: t('syncProject.no.'),
        cell: (info) => {
          return info.row.index + 1;
        },
        size: 50,
      },
      {
        accessorKey: 'display_name',
        header: t('syncProject.projectName'),
        cell: (info) => (
          <Text
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              _hover: {
                textDecoration: 'underline',
              },
            }}
            onClick={(e) => handleOpenProject(e, info.row.original.name)}
          >
            {info.getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: 'name',
        header: t('syncProject.url'),
        cell: (info) => (
          <Tooltip label={info.getValue() as string}>
            <Text
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {info.getValue() as string}
            </Text>
          </Tooltip>
        ),
      },
    ],
    [t]
  );

  const handleOpenProject = (
    e: React.MouseEvent<HTMLParagraphElement, MouseEvent>,
    projectName: string
  ) => {
    e.preventDefault();
    dispatch(synchronizationAction.updateCurrentProject(projectName));
    dispatch(synchronizationAction.fetchDocumentListByProject(''));
    history.push('/sync-documents');
  };

  const handleSelectChange = (item: any) => {
    if (item.value) {
      dispatch(synchronizationAction.updateCurrentConnection(item.value));
    }
  };

  const selectedOption = useMemo(() => {
    const selected = connections.list?.find((item) => item.id === current?.connection_id);
    return selected
      ? {
          value: selected.id,
          label: selected.display_name,
        }
      : {};
  }, [connections, current.connection_id]);

  return (
    <>
      <Stack>
        <Flex
          mb={{
            base: '0.8rem',
            md: '1rem',
            xl: '1.25rem',
            '2xl': '1.25rem',
          }}
        >
          <Heading as='h3' mr='1rem' fontWeight={'bold'} size='md'>
            {t('syncProject.yourProject')}
          </Heading>
        </Flex>

        <VStack alignItems={'flex-start'} spacing={3}>
          <HStack
            sx={{
              width: '100%',
            }}
          >
            <Box sx={{ width: '35%' }}>
              <Select
                options={connections.select_options}
                onChange={handleSelectChange}
                value={selectedOption}
                chakraStyles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                noOptionsMessage={() => {
                  return t('common.noOptions');
                }}
              />
            </Box>
            <Link to={'/synchronization'}>
              <Button
                sx={{
                  color: 'gray.500',
                  fontWeight: 'bold',
                }}
                leftIcon={<IoMdReturnLeft />}
                variant='amatrium_submit'
              >
                {t('syncProject.viewList')}
              </Button>
            </Link>
          </HStack>
          <Box
            sx={{
              width: '100%',
            }}
            border={mode('0.5px solid rgb(209, 213, 219)', '')}
          >
            {isLoading ? (
              <AmatriumSkeleton options={null} />
            ) : (
              <>
                {projects.list.length ? (
                  <ReactTable columns={columns} data={projects.list} />
                ) : (
                  <HStack justifyContent={'center'}>
                    <Empty content={''} />
                  </HStack>
                )}
              </>
            )}
          </Box>
        </VStack>
      </Stack>
    </>
  );
};

export default ProjectList;
